import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { storage } from '../firebase'

/**
 * Configuration options for file upload
 */
export interface FileUploadConfig {
  /** Base path in Firebase Storage (e.g., "profile-images", "certificates") */
  storageBasePath: string
  /** Whether to compress the file before upload (only works for images) */
  shouldCompress?: boolean
  /** Maximum file size in bytes */
  maxFileSize?: number
  /** Allowed MIME types */
  allowedTypes?: string[]
  /** Callback for progress updates (0-100) */
  onProgress?: (progress: number) => void
  /** Callback for status message updates */
  onStatusChange?: (status: string) => void
  /** Additional metadata to store with the file */
  customMetadata?: Record<string, string>
}

/**
 * Image compression configuration
 */
const IMAGE_COMPRESSION = {
  maxWidth: 800,
  maxHeight: 800,
  quality: 0.85,
  format: 'image/jpeg' as const,
}

/**
 * Upload timeout in milliseconds
 */
const UPLOAD_TIMEOUT = 300000 // 5 minutes (was 15 seconds) - increased for large video uploads

/**
 * Compress an image file before upload
 */
async function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image()

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width
        let height = img.height

        if (
          width > IMAGE_COMPRESSION.maxWidth ||
          height > IMAGE_COMPRESSION.maxHeight
        ) {
          if (width > height) {
            height = Math.round(height * (IMAGE_COMPRESSION.maxWidth / width))
            width = IMAGE_COMPRESSION.maxWidth
          } else {
            width = Math.round(width * (IMAGE_COMPRESSION.maxHeight / height))
            height = IMAGE_COMPRESSION.maxHeight
          }
        }

        // Create canvas for resizing
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          URL.revokeObjectURL(img.src)
          reject(new Error('Could not get canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0, width, height)

        // Convert to blob with specified quality
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src)
            if (!blob) {
              reject(new Error('Failed to compress image'))
              return
            }
            resolve(blob)
          },
          IMAGE_COMPRESSION.format,
          IMAGE_COMPRESSION.quality,
        )
      }

      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        reject(new Error('Failed to load image for compression'))
      }

      img.src = URL.createObjectURL(file)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Upload a file to Firebase Storage with progress tracking and error handling
 *
 * @param file - The file to upload
 * @param userId - The user's unique ID
 * @param config - Upload configuration options
 * @returns Promise that resolves to the download URL
 *
 * @example
 * ```typescript
 * const url = await uploadFileToStorage(file, currentUser.uid, {
 *   storageBasePath: 'certificates/credential-123',
 *   shouldCompress: false,
 *   maxFileSize: 10 * 1024 * 1024, // 10MB
 *   allowedTypes: ['application/pdf', 'image/jpeg'],
 *   onProgress: (progress) => setProgress(progress),
 *   onStatusChange: (status) => setStatus(status),
 * })
 * ```
 */
export async function uploadFileToStorage(
  file: File,
  userId: string,
  config: FileUploadConfig,
): Promise<string> {
  const {
    storageBasePath,
    shouldCompress = false,
    maxFileSize,
    allowedTypes,
    onProgress,
    onStatusChange,
    customMetadata = {},
  } = config

  // Validate user ID
  if (!userId) {
    throw new Error('User ID is required for file upload')
  }

  // Validate file type
  if (allowedTypes && !allowedTypes.includes(file.type)) {
    throw new Error(
      `Invalid file type: ${file.type}. Allowed types: ${allowedTypes.join(', ')}`,
    )
  }

  // Validate file size
  if (maxFileSize && file.size > maxFileSize) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(2)
    const maxSizeMB = (maxFileSize / 1024 / 1024).toFixed(2)
    throw new Error(
      `File size (${sizeMB} MB) exceeds maximum allowed size (${maxSizeMB} MB)`,
    )
  }

  return new Promise(async (resolve, reject) => {
    let uploadTask: any = null
    let timeoutId: NodeJS.Timeout | null = null

    try {
      // Prepare file for upload
      let fileToUpload: File | Blob = file

      // Compress image if requested and file is an image
      if (shouldCompress && file.type.startsWith('image/')) {
        try {
          onStatusChange?.('Compressing image...')
          const compressedBlob = await compressImage(file)
          fileToUpload = new File([compressedBlob], file.name, {
            type: IMAGE_COMPRESSION.format,
            lastModified: Date.now(),
          })
        } catch (compressError) {
          console.warn(
            'Image compression failed, using original file:',
            compressError,
          )
          // Continue with original file if compression fails
        }
      }

      // Create unique filename with timestamp
      const timestamp = Date.now()
      const sanitizedFilename = file.name.replace(/\s+/g, '_')
      const filename = `${timestamp}_${sanitizedFilename}`

      // Create storage reference
      const storageRef = ref(storage, `${storageBasePath}/${filename}`)

      // Prepare metadata
      const metadata = {
        contentType: fileToUpload.type,
        customMetadata: {
          uploadedBy: userId,
          originalFilename: file.name,
          timestamp: timestamp.toString(),
          ...customMetadata,
        },
      }

      // Set timeout to prevent infinite waiting
      timeoutId = setTimeout(() => {
        if (uploadTask) {
          try {
            uploadTask.cancel()
          } catch (e) {
            console.warn('Failed to cancel upload task:', e)
          }
        }
        reject(
          new Error(`Upload timed out after ${UPLOAD_TIMEOUT / 1000} seconds`),
        )
      }, UPLOAD_TIMEOUT)

      // Start upload with progress tracking
      onStatusChange?.('Uploading...')
      onProgress?.(0)

      uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata)

      // Track upload progress
      uploadTask.on(
        'state_changed',
        (snapshot: any) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          onProgress?.(Math.round(progress))

          // Update status message based on progress
          if (progress < 25) {
            onStatusChange?.('Starting upload...')
          } else if (progress < 50) {
            onStatusChange?.('Uploading...')
          } else if (progress < 75) {
            onStatusChange?.('Almost there...')
          } else if (progress < 100) {
            onStatusChange?.('Finalizing...')
          }
        },
        (error: any) => {
          // Handle upload error
          if (timeoutId) clearTimeout(timeoutId)
          console.error('Upload error:', error)

          // Provide more specific error messages
          let errorMessage = 'Upload failed'

          if (error.code === 'storage/unauthorized') {
            errorMessage =
              'Upload failed: You do not have permission to upload files. Please check your authentication.'
          } else if (error.code === 'storage/canceled') {
            errorMessage = 'Upload was canceled'
          } else if (error.code === 'storage/unknown') {
            errorMessage =
              'Upload failed due to a network error. This may be a CORS issue. Please try again or contact support.'
          } else if (error.message && error.message.includes('CORS')) {
            errorMessage =
              'Upload failed due to CORS policy. Please contact support to configure Firebase Storage CORS settings.'
          } else if (error.message) {
            errorMessage = `Upload failed: ${error.message}`
          }

          reject(new Error(errorMessage))
        },
        async () => {
          // Handle successful upload
          if (timeoutId) clearTimeout(timeoutId)

          try {
            onStatusChange?.('Getting download URL...')
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
            onStatusChange?.('Upload complete!')
            onProgress?.(100)
            resolve(downloadURL)
          } catch (error: any) {
            reject(new Error(`Failed to get download URL: ${error.message}`))
          }
        },
      )
    } catch (error: any) {
      if (timeoutId) clearTimeout(timeoutId)
      reject(error)
    }
  })
}

/**
 * Helper function to validate file before upload
 * Returns error message if validation fails, null if valid
 */
export function validateFile(
  file: File,
  config: Pick<FileUploadConfig, 'maxFileSize' | 'allowedTypes'>,
): string | null {
  const { maxFileSize, allowedTypes } = config

  // Check file type
  if (allowedTypes && !allowedTypes.includes(file.type)) {
    return `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`
  }

  // Check file size
  if (maxFileSize && file.size > maxFileSize) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(2)
    const maxSizeMB = (maxFileSize / 1024 / 1024).toFixed(2)
    return `File size (${sizeMB} MB) exceeds maximum (${maxSizeMB} MB)`
  }

  return null
}
